import { isEqual, isUndefined, omitBy } from 'lodash-es';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { useRouter } from 'next/router';
import { SearchFilters, SearchInput } from './collection-search-helpers';
import {
  generateSearchParams,
  searchInputReducer,
  searchToQuery,
  updateQueryString,
} from '@/components/collections-v2/collection-nav-page/helpers';

export default function useFilters({
  fixedFilters,
  searchInput,
}: {
  fixedFilters: SearchFilters;
  searchInput: SearchInput;
}) {
  const router = useRouter();

  const [searchApiQs, setSearchApiQs] = useState<string>('');

  const handle = router.query.handle as string;
  const pk = router.query.pk as string;
  const searchParameters = generateSearchParams(searchInput, fixedFilters);

  const initialSearchParams = {
    ...searchInput,
    ...fixedFilters,
    variantFilters: {
      ...searchParameters,
    },
  };

  const [searchParams, dispatchSearchInput] = useReducer(
    searchInputReducer,
    initialSearchParams,
  );

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(searchToQuery(searchParams));

    urlSearchParams.sort();
    const qs = urlSearchParams.toString();

    setSearchApiQs(qs);
    updateQueryString({
      searchInput: searchParams,
      router,
      handle,
      pk,
    });
  }, [searchParams]);

  const shouldShowClearAll = useMemo(() => {
    const variantFilters = omitBy(searchParams.variantFilters, isUndefined);
    // Check if any filter has a length > 0 and the filters are not equal to the fixed filters
    const hasActiveFilters = Object.values(variantFilters).some(
      (filterArray) => filterArray && filterArray.length > 0,
    );

    const searchKeys = Object.keys(searchParams.variantFilters).filter(
      (f) => searchParams.variantFilters[f]?.length,
    );
    const fixedKeys = Object.keys(fixedFilters.variantFilters);

    const isOnlyFixedFilters =
      searchKeys.length === fixedKeys.length &&
      searchKeys.every((key) => fixedKeys.includes(key));

    const filtersChanged = !isEqual(
      fixedFilters.variantFilters,
      variantFilters,
    );

    return hasActiveFilters && filtersChanged && !isOnlyFixedFilters;
  }, [searchParams.variantFilters, fixedFilters.variantFilters]);

  const numberOfFilters = useMemo(
    () =>
      Object.entries(omitBy(searchParams.variantFilters, isUndefined))
        .filter(([filter, searchFilter]) => {
          const fixedFilter = fixedFilters.variantFilters[filter];

          // Only include filters that are not equal to the fixedFilters
          return (
            searchFilter &&
            searchFilter.length > 0 &&
            !isEqual(searchFilter, fixedFilter)
          );
        })
        .reduce(
          (total, [, searchFilter]) => total + (searchFilter ?? []).length,
          0,
        ),
    [searchParams.variantFilters, fixedFilters.variantFilters],
  );

  return {
    numberOfFilters,
    shouldShowClearAll,
    searchParams,
    dispatchSearchInput,
    searchApiQs,
  };
}
